import { useContext } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  ColDef,
  ColumnMenuTab,
  ICellRendererParams,
  ValueGetterParams
} from 'ag-grid-community';
import { decodeGetter } from '@/app/common/components/ag-grid/configs';
import moment from 'jalali-moment';
import { RemoveGscContext } from '@/context/Context';
import { Dropdown } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import gscApi, {
  useGetDataFromScMutation
} from '@/app/KeywordResearch/api/gscApi';
import type { GscHistoryItem } from '@/app/KeywordResearch/types/gscApiTypes';
import { errorMessage, successMessage, warningMessage } from '@/utils/toast';
import { getColor, toPersianDigit } from '@/helpers/utils';
import useDomain from '@/hooks/useDomain';
import GscStatusRenderer, {
  getGscStatusConfig
} from '@/app/common/components/CellRenderers/GscStatusRenderer';

const menuTabs: ColumnMenuTab[] = [
  'columnsMenuTab',
  'filterMenuTab',
  'generalMenuTab'
];

const formatGregorianDateToJalali = (value: string | null | undefined) => {
  if (!value) return '';
  try {
    return toPersianDigit(moment(value).locale('fa').format('YYYY/MM/DD'));
  } catch {
    return value;
  }
};

type GscHistoryColumn = ColDef<GscHistoryItem> & {
  searchable?: boolean;
  title?: string;
  type?: string;
  noFilter?: boolean;
};

type RemoveGscContextValue = {
  domain: string;
  setDomain: (domain: string) => void;
  account: string;
  setAccount: (account: string) => void;
  hostNameId: string;
  setHostNameId: (hostNameId: string) => void;
  show: boolean;
  setShow: (show: boolean) => void;
};

export const gscHistoryColumnConfig: GscHistoryColumn[] = [
  {
    field: 'host_name',
    type: 'string',
    title: 'دامنه',
    flex: 1,
    minWidth: 150,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    cellRenderer: (params: ICellRendererParams<GscHistoryItem>) => {
      const { domain_slug, domain } = useDomain();
      const content = (
        <div className="d-flex gap-1 align-items-baseline">
          {params.value}
        </div>
      );

      if (params.data?.status === 'permission_denied' || params.data?.status === 'no_data') {
        return (
          <span className="fw-bolder btn-link disabled-link">{content}</span>
        );
      }

      return (
        <Link
          to={`/keyword-research/keyword-gsc/${domain_slug}/${domain}/${params.data?.host_name_id}`}
          style={{
            color: getColor('navbar-vertical-default-link-active-color')
          }}
          className="fw-bolder btn-link"
        >
          {content}
        </Link>
      );
    },
    filterValueGetter: decodeGetter
  },
  {
    field: 'query_count',
    type: 'number',
    title: 'تعداد کلمات',
    width: 150,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs
  },
  {
    field: 'account_title',
    type: 'string',
    title: 'نام اکانت',
    width: 150,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs
  },
  {
    field: 'status',
    type: 'string',
    title: 'وضعیت',
    width: 140,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    filterValueGetter: (params: ValueGetterParams<GscHistoryItem>) =>
      getGscStatusConfig(params.data?.status).label,
    cellRenderer: (params: ICellRendererParams<GscHistoryItem>) => (
      <GscStatusRenderer status={params.value} />
    )
  },
  {
    field: 'updated_at',
    type: 'string',
    title: 'به‌روزرسانی',
    width: 150,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    valueFormatter: params => formatGregorianDateToJalali(params.value)
  },
  {
    field: 'inserted_at',
    type: 'string',
    title: 'تاریخ درج',
    width: 130,
    searchable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    menuTabs,
    valueFormatter: params => formatGregorianDateToJalali(params.value)
  },
  {
    field: 'url',
    type: 'array',
    title: ' ',
    searchable: false,
    cellStyle: { textAlign: 'center', margin: '0px' },
    cellClass: 'over-flow',
    width: 70,
    pinned: 'left',
    noFilter: true,
    cellRenderer: (params: ICellRendererParams<GscHistoryItem>) => {
      const { domain_slug, domain } = useDomain();
      const dispatch = useDispatch();
      const [refreshUserGscHistory] = useGetDataFromScMutation();
      const { setAccount, setDomain, setHostNameId, setShow } =
        useContext(RemoveGscContext) as RemoveGscContextValue;

      return (
        <span className="allaho_akbar_zIndex">
          <Dropdown
            onClick={e => e.stopPropagation()}
            align="end"
            className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
          >
            <Dropdown.Toggle
              className="text-600 btn-reveal"
              split
              variant="reveal"
              size="sm"
            >
              <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
            </Dropdown.Toggle>
            <Dropdown.Menu className="border py-0">
            <Dropdown.Item
                  as={Link}
                  to={`/keyword-research/keyword-gsc/${domain_slug}/${domain}/${params.data?.host_name_id}`}
                  className={classNames(
                    'link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1'
                  )}
                >
                  <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                  مشاهده
                </Dropdown.Item>
              <Dropdown.Item
                onClick={() => {
                  if (!params.data) return;

                  refreshUserGscHistory({
                    host_name_id: params.data.host_name_id,
                    account: params.data.account
                  })
                    .unwrap()
                    .then(({ data }) => {
                      if (data?.status) {
                        if (data.status === '400') {
                          errorMessage('دامنه درخواستی هیچ اطلاعاتی ندارد.');
                        } else if (data.status === '403') {
                          errorMessage(
                            `اکانت ${params.data?.account} دسترسی لازم برای گرفتن اطلاعات از سرچ کنسول رو نداره، در صورت نیاز لطفا دوباره دسترسی بدهید.`
                          );
                        } else if (data.status === 'duplicated request') {
                          warningMessage(
                            'درخواست برای این دامنه تکراری است. لطفا برای درخواست مجدد 24 ساعت بعد تلاش کنید.'
                          );
                        } else {
                          dispatch(
                            gscApi.util.invalidateTags(['userGscHistory'])
                          );
                          successMessage(' عملیات با موفقیت انجام شد.');
                        }
                      }
                    })
                    .catch(err => {
                      console.error('Error fetching data:', err);
                      errorMessage('در انجام عملیات خطایی رخ داد.');
                    });
                }}
                className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="undo-alt" className="me-1 ms-2" />
                گرفتن مجدد کلمات
              </Dropdown.Item>
              <Dropdown.Divider className="my-0" />
              <Dropdown.Item
                onClick={() => {
                  if (!params.data) return;

                  setAccount(params.data.account);
                  setDomain(params.data.host_name);
                  setHostNameId(params.data.host_name_id);
                  setShow(true);
                }}
                className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
              >
                <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
                حذف
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </span>
      );
    }
  }
];
